import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { Send } from "lucide-react"

import { Button } from "@/components/ui/Button"
import { Spinner } from "@/components/ui/Spinner"
import { Textarea } from "@/components/ui/Textarea"

interface Message {
  role: "assistant" | "user"
  content: string
  timestamp: Date
}

interface SummaryStepChatProps {
  initialSummary: string
  isInitialLoading: boolean
  isSubmitting?: boolean
  onConfirm: () => void
  onBack: () => void
  onRefineRequest: (userMessage: string) => Promise<string>
}

const REFINING_MESSAGES = [
  "Analyzing your request...",
  "Refining assignment...",
  "Generating new questions...",
  "Adjusting the assignment...",
  "Crafting the assignment...",
  "Almost there...",
]

function RefiningMessage() {
  const [messageIndex, setMessageIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % REFINING_MESSAGES.length)
    }, 2000)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="tw-flex tw-justify-start">
      <div className="tw-bg-gray-100 tw-rounded-lg tw-p-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Spinner className="tw-w-4 tw-h-4" />
          <span className="tw-text-sm tw-text-gray-600">
            {REFINING_MESSAGES[messageIndex]}
          </span>
        </div>
      </div>
    </div>
  )
}

export function SummaryStep({
  initialSummary,
  isInitialLoading,
  isSubmitting = false,
  onConfirm,
  onBack,
  onRefineRequest,
}: SummaryStepChatProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState("")
  const [isRefining, setIsRefining] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const hasInitialized = useRef(false)

  // Initialize with the first assistant message
  useEffect(() => {
    if (initialSummary && !hasInitialized.current) {
      setMessages([
        {
          role: "assistant",
          content: initialSummary,
          timestamp: new Date(),
        },
      ])
      hasInitialized.current = true
    }
  }, [initialSummary])

  // Auto-scroll to bottom when new messages arrive
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight
    }
  }, [messages])

  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim()
    if (!trimmedInput || isRefining) return

    const userMessage: Message = {
      role: "user",
      content: trimmedInput,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue("")
    setIsRefining(true)

    try {
      const refinedSummary = await onRefineRequest(trimmedInput)

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: refinedSummary,
          timestamp: new Date(),
        },
      ])
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "There was an error processing your request. Please try again.",
          timestamp: new Date(),
        },
      ])
    } finally {
      setIsRefining(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const isDisabled = isRefining || isSubmitting
  const hasMessages = messages.length > 0

  return (
    <div className="tw-text-center tw-space-y-8 tw-max-w-3xl tw-mx-auto">
      <h2 className="tw-text-3xl tw-font-bold tw-text-gray-800">
        Assignment Preview
      </h2>

      {/* Chat Messages Container */}
      <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="tw-max-h-[200px] tw-overflow-y-auto tw-p-6"
        >
          {isInitialLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-8">
              <Spinner className="tw-w-8 tw-h-8" />
              <p className="tw-text-gray-600">
                Generating your assignment preview...
              </p>
            </div>
          ) : (
            <div className="tw-space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`tw-flex ${
                    message.role === "user"
                      ? "tw-justify-end"
                      : "tw-justify-start"
                  }`}
                >
                  <div
                    className={`tw-max-w-[80%] tw-rounded-lg tw-p-4 ${
                      message.role === "user"
                        ? "tw-bg-emerald-600 tw-text-white"
                        : "tw-bg-gray-100 tw-text-gray-800"
                    }`}
                  >
                    <div
                      className={`tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed ${message.role === "user" ? "tw-text-right" : "tw-text-left"}`}
                    >
                      {message.content}
                    </div>
                    <div
                      className={`tw-text-xs tw-mt-2 tw-opacity-70 ${
                        message.role === "user"
                          ? "tw-text-white tw-text-right"
                          : "tw-text-gray-500 tw-text-left"
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              ))}
              {isRefining && <RefiningMessage />}
            </div>
          )}
        </div>

        {!isInitialLoading && (
          <div className="tw-border-t tw-border-gray-200 tw-p-4 tw-bg-gray-50">
            <div className="tw-flex tw-gap-3 tw-items-end">
              <Textarea
                id="assignment-summary"
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Request changes or improvements... (Press Enter to send, Shift+Enter for new line)"
                className="tw-flex-1 tw-min-h-[48px] tw-max-h-[120px] tw-w-full tw-max-w-2xl tw-mx-auto tw-p-2 tw-border tw-rounded-sm tw-text-center tw-text-sm tw-text-gray-700 tw-transition-all tw-resize-none tw-overflow-y-auto focus:tw-outline-none focus:tw-ring-2 tw-border-emerald-600 focus:tw-border-emerald-600 focus:tw-ring-emerald-600"
                disabled={isDisabled}
                rows={2}
              />
              <Button
                type="button"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isDisabled}
                className="tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-rounded-lg tw-px-4 tw-py-3"
                data-testid="send-chat-button"
              >
                <Send className="tw-w-4 tw-h-4" />
              </Button>
            </div>
            <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
              💡 Tip: Ask for more questions, different difficulty levels, or
              specific topic changes
            </p>
          </div>
        )}
      </div>

      {!isInitialLoading && hasMessages && (
        <div className="tw-flex tw-justify-center tw-gap-4">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            size="lg"
            className="tw-rounded-full tw-min-w-[140px]"
            disabled={isDisabled}
          >
            Re-edit Prompt
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            size="lg"
            className="tw-rounded-full tw-min-w-[140px] tw-bg-emerald-600 hover:tw-bg-emerald-800"
            disabled={isDisabled}
          >
            {isSubmitting ? (
              <span className="tw-flex tw-items-center tw-gap-2">
                <Spinner className="tw-w-4 tw-h-4" />
                Creating...
              </span>
            ) : (
              "Create Assignment"
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
